import { CommonHeader } from "@bcc/components/common-header";
import { InventoryStats } from "@bcc/components/inventory/inventory-stats";
import { InventoryTable } from "@bcc/components/inventory/inventory-table";
import { Button } from "@bcc/components/ui/button";
import { Input } from "@bcc/components/ui/input";
import { Download, Filter, Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { setInventoryViewTestId } from "./inventory-view.utils";

interface StatData {
  name: string;
  value: string;
  icon: string;
  iconBg: string;
  iconColor: string;
}

interface InventoryItem {
  id: number;
  product: string;
  sku: string;
  current: number;
  minimum: number;
  maximum: number;
  location: string;
  status: "in-stock" | "low-stock" | "out-of-stock";
}

interface InventoryViewProps {
  statsData: StatData[];
  inventoryData: InventoryItem[];
  testId?: string;
}

export function InventoryView({ statsData, inventoryData, testId }: Readonly<InventoryViewProps>) {
  const { t } = useTranslation();

  return (
    <div data-testid={testId ?? setInventoryViewTestId("container")} className="space-y-6">
      <CommonHeader
        title={t("views.inventory.title")}
        description={t("views.inventory.description")}
        actions={
          <>
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              {t("views.inventory.export")}
            </Button>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="mr-2 h-4 w-4" />
              {t("views.inventory.addStock")}
            </Button>
          </>
        }
      >
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input type="search" placeholder={t("views.inventory.searchPlaceholder")} className="pl-10" />
          </div>
          <Button variant="outline">
            <Filter className="mr-2 h-4 w-4" />
            {t("common.filter")}
          </Button>
        </div>
      </CommonHeader>
      <InventoryStats data={statsData} />
      <InventoryTable data={inventoryData} />
    </div>
  );
}
